/**
 * The user's page layout ("Rozložení stránek" in the profile menu, RD-2957): list only, or a list
 * with the detail beside it (vertical split) or below it (horizontal split). Global for the user and
 * honoured by the pages that support a split; kept in this browser only, with the splitter ratios.
 */
export type PageLayout = 'list' | 'vertical' | 'horizontal';
export type SplitLayout = Exclude<PageLayout, 'list'>;

export interface PageLayoutPrefs {
  layout: PageLayout;
  /** Share of the list pane in %, per split direction — dragging one doesn't move the other. */
  ratio: Record<SplitLayout, number>;
}

/** Below this a pane is too small to use; the splitter won't drag past it either way. */
export const MIN_PANE_PERCENT = 25;
export const MAX_PANE_PERCENT = 100 - MIN_PANE_PERCENT;

/** Figma 21940:60880 gives the list about 45% beside the detail; stacked, the list gets the upper 40%. */
export const DEFAULT_PAGE_LAYOUT: PageLayoutPrefs = { layout: 'list', ratio: { vertical: 45, horizontal: 40 } };

const LAYOUTS: PageLayout[] = ['list', 'vertical', 'horizontal'];

export const clampPaneRatio = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(Math.min(MAX_PANE_PERCENT, Math.max(MIN_PANE_PERCENT, value)) * 10) / 10 : fallback;

/** Whatever storage holds, as prefs the page can use; anything unrecognised falls back to the defaults. */
export const sanitizePageLayoutPrefs = (raw: unknown): PageLayoutPrefs => {
  const source = raw && typeof raw === 'object' ? (raw as Partial<PageLayoutPrefs>) : {};
  const ratio = source.ratio && typeof source.ratio === 'object' ? source.ratio : ({} as Partial<Record<SplitLayout, number>>);
  return {
    layout: LAYOUTS.includes(source.layout as PageLayout) ? (source.layout as PageLayout) : DEFAULT_PAGE_LAYOUT.layout,
    ratio: {
      vertical: clampPaneRatio(ratio.vertical, DEFAULT_PAGE_LAYOUT.ratio.vertical),
      horizontal: clampPaneRatio(ratio.horizontal, DEFAULT_PAGE_LAYOUT.ratio.horizontal),
    },
  };
};

export const pageLayoutStorageKey = (userId: string) => `user_${userId}_page_layout`;
